import {
  computed,
  inject,
  Injector,
  runInInjectionContext,
} from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { from } from 'rxjs';
import { map, switchMap, tap } from 'rxjs';
import { DeepPartial } from '@apollo/client/utilities';
import { initialStagingState } from './staging.state';
import {
  DeleteStagingSottoscrizioneGQL,
  GetStagingLottiDocument,
  GetStagingLottiSubscription,
  GetStagingSottoscrizioneGQL,
  GetStagingSottoscrizioniGQL,
  InsertOrUpdateStagingLottoGQL,
  InsertOrUpdateStagingSottoscrizioneGQL,
  RefreshMvNowDocument,
  Staging__Stato_Sottoscrizione_Enum,
  Staging_Gestore_Lotto_Insert_Input,
  Staging_Gestore_Lotto_On_Conflict,
  Staging_Sottoscrizione_Insert_Input,
  Staging_Sottoscrizione_On_Conflict,
} from '../../../../../_core/_graphql/generated';
import { ApolloRoleService } from '../../../../../_core/_services/apollo-role.service';
import { UserStore } from '../../../_store/user.store';
import { LoadingService } from '../../../../../_core/_services/loading.service';

export const StagingStore = signalStore(
  { providedIn: 'root' },
  withState(initialStagingState),
  withComputed((store) => ({})),
  withMethods((store, injector = inject(Injector)) => {
    // Registry in-memory (non nel state serializzato)
    const subs = new Map<string, { unsubscribe: () => void }>();

    const setSub = (key: string, sub: { unsubscribe: () => void }) => {
      // se esiste, chiudi quella precedente
      subs.get(key)?.unsubscribe?.();
      subs.set(key, sub);
    };

    const clearSub = (key: string) => {
      subs.get(key)?.unsubscribe?.();
      subs.delete(key);
    };

    const clearAllSubs = (prefix?: string) => {
      for (const [k, s] of subs.entries()) {
        if (!prefix || k.startsWith(prefix)) {
          s?.unsubscribe?.();
          subs.delete(k);
        }
      }
    };

    const refreshSottoscrizioniMv = () => {
      return runInInjectionContext(injector, () => {
        const _apollo = inject(ApolloRoleService);
        const _getter = _apollo.getClient(store.role?.()!);
        return from(
          _getter.mutate({
            mutation: RefreshMvNowDocument,
            context: {
              headers: { 'x-hasura-role': store.role?.() },
            },
          }),
        );
      });
    };

    const fetchSottoscrizioni = () => {
      return runInInjectionContext(injector, () => {
        const _getter = inject(GetStagingSottoscrizioniGQL);
        const lotto_id = store.sottoscrizioni_lotto_id?.();
        return _getter
          .fetch(
            {
              where: {
                stato: { _neq: Staging__Stato_Sottoscrizione_Enum.Completata },
                ...(lotto_id ? { lotto_id: { _eq: lotto_id } } : {}),
              },
            },
            {
              context: {
                headers: { 'x-hasura-role': store.role?.() },
              },
            },
          )
          .pipe(
            tap((res) => {
              patchState(store, {
                sottoscrizioni_data: res.data?.staging_sottoscrizione_list_mv,
                sottoscrizioni_loading: false,
                sottoscrizioni_version: (store.sottoscrizioni_version?.() ?? 0) + 1,
              });
            }),
          );
      });
    };

    return {
      init(
        role:
          | 'user'
          | 'manager'
          | 'anonymous'
          | 'installatore'
          | 'tecnico'
          | 'segnalatore'
          | 'amico',
        lotto_enabled: boolean,
      ) {
        patchState(store, {
          role,
          lotto_enabled,
        });
      },
      initSottoscrizioni(lotto_id?: string) {
        return runInInjectionContext(injector, () => {
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          patchState(store, { sottoscrizioni_loading: true, sottoscrizioni_lotto_id: lotto_id });

          return fetchSottoscrizioni().subscribe({
            error: (error) => {
              patchState(store, {
                sottoscrizioni_data: undefined,
                sottoscrizioni_loading: false,
                failure: error,
              });
              _toast.add({
                key: 'global-toast',
                severity: 'error',
                summary: _translate.instant(`toast.error.title`),
                detail: _translate.instant(`toast.error.detail`, {
                  error,
                }),
              });
            },
          });
        });
      },

      initLotti() {
        return runInInjectionContext(injector, () => {
          const _apollo = inject(ApolloRoleService);
          const _getter = _apollo.getClient(store.role?.()!);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          clearSub('stagingLotti');

          const sub = _getter
            .subscribe<GetStagingLottiSubscription>({
              query: GetStagingLottiDocument,
              variables: {
                where: {},
              },
            })
            .subscribe({
              next: (res) => {
                patchState(store, {
                  lotti_data: res.data?.staging_lotto,
                  lotti_loading: false,
                });
              },
              error: (error) => {
                patchState(store, {
                  lotti_data: undefined,
                  lotti_loading: false,
                  failure: error,
                });
                _toast.add({
                  key: 'global-toast',
                  severity: 'error',
                  summary: _translate.instant(`toast.error.title`),
                  detail: _translate.instant(`toast.error.detail`, {
                    error,
                  }),
                });
              },
            });

          setSub('stagingLotti', sub);
          return sub;
        });
      },

      deleteSottoscrizione: (data: string[]) => {
        return runInInjectionContext(injector, () => {
          const _delete = inject(DeleteStagingSottoscrizioneGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          return _delete
            .mutate(
              {
                where: {
                  id: {
                    _in: data,
                  },
                },
              },
              {
                context: {
                  headers: { 'x-hasura-role': store.role?.() },
                },
              },
            )
            .pipe(
              switchMap((res) =>
                refreshSottoscrizioniMv().pipe(
                  switchMap(() => fetchSottoscrizioni()),
                  map(() => res),
                ),
              ),
              tapResponse({
                next: (res) => res,
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', {
                      error,
                    }),
                  });
                },
              }),
            );
        });
      },
      insertOrUpdateSottoscrizione: (
        objects: Staging_Sottoscrizione_Insert_Input[],
        on_conflict: Staging_Sottoscrizione_On_Conflict,
      ) => {
        return runInInjectionContext(injector, () => {
          const _inserOrUpdate = inject(InsertOrUpdateStagingSottoscrizioneGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);

          const sanitizeIndirizzoData = (value: any): any => {
            if (!value || typeof value !== 'object') return value;

            const next: any = Array.isArray(value) ? [] : {};
            for (const [key, raw] of Object.entries(value)) {
              if (key === '__typename') continue;
              if (
                key === 'citta' ||
                key === 'comune' ||
                key === 'city' ||
                key === 'locality' ||
                key === 'geom'
              ) {
                continue;
              }

              if (Array.isArray(raw)) {
                next[key] = raw.map((entry) => sanitizeIndirizzoData(entry));
                continue;
              }

              if (raw && typeof raw === 'object') {
                next[key] = sanitizeIndirizzoData(raw);
                continue;
              }

              next[key] = raw;
            }

            return next;
          };

          const sanitizeMutationObjects = (
            value: any,
            parentKey?: string,
          ): any => {
            if (Array.isArray(value)) {
              return value.map((entry) =>
                sanitizeMutationObjects(entry, parentKey),
              );
            }

            if (value instanceof Date) return value;
            if (!value || typeof value !== 'object') return value;

            const next: any = {};
            for (const [key, raw] of Object.entries(value)) {
              if (key === '__typename') continue;

              if (parentKey === 'indirizzo' && key === 'data') {
                next[key] = sanitizeIndirizzoData(raw);
                continue;
              }

              next[key] = sanitizeMutationObjects(raw, key);
            }

            return next;
          };

          const sanitizedObjects = sanitizeMutationObjects(
            objects,
          ) as Staging_Sottoscrizione_Insert_Input[];

          return _inserOrUpdate
            .mutate(
              {
                objects: sanitizedObjects,
                on_conflict,
              },
              {
                context: {
                  headers: { 'x-hasura-role': store.role?.() },
                },
              },
            )
            .pipe(
              switchMap((res) =>
                refreshSottoscrizioniMv().pipe(
                  switchMap(() => fetchSottoscrizioni()),
                  map(() => res),
                ),
              ),
              tapResponse({
                next: (res) => res,
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', {
                      error,
                    }),
                  });
                },
              }),
            );
        });
      },
      loadSelectedSottoscrizione: (id: string) => {
        const _getter = inject(GetStagingSottoscrizioneGQL);
        const _toast = inject(MessageService);
        const _translate = inject(TranslateService);

        return runInInjectionContext(injector, () => {
          return _getter.fetch(
            { id },
            {
              context: {
                headers: { 'x-hasura-role': store.role?.() },
              },
            },
          );
        }).subscribe({
          next: (res) => {
            patchState(store, {
              selected_sottoscrizione_loading: false,
              selected_sottoscrizione: res.data?.staging_sottoscrizione_by_pk,
            });
          },
          error: (error) => {
            patchState(store, {
              selected_sottoscrizione_loading: false,
              selected_sottoscrizione: undefined,
              failure: error,
            });
            _toast.add({
              key: 'global-toast',
              severity: 'error',
              summary: _translate.instant(`toast.error.title`),
              detail: _translate.instant(`toast.error.detail`, {
                error,
              }),
            });
          },
        });
      },
      clearUi: () => {
        patchState(store, {
          tabella_sottoscrizioni_ui: undefined,
        });
      },

      clearSelectedSottoscrizione: () => {
        patchState(store, {
          selected_sottoscrizione_loading: true,
          selected_sottoscrizione: undefined,
        });
      },
      insertOrUpdateStagingLotto: (
        objects: Staging_Gestore_Lotto_Insert_Input,
        on_conflict: Staging_Gestore_Lotto_On_Conflict,
      ) => {
        return runInInjectionContext(injector, () => {
          const _insertOrUpdate = inject(InsertOrUpdateStagingLottoGQL);
          const _toast = inject(MessageService);
          const _translate = inject(TranslateService);
          const _userStore = inject(UserStore);
          const _loading = inject(LoadingService);

          return _insertOrUpdate
            .mutate(
              {
                objects: {
                  ...objects,
                  auth_id: _userStore.user_data?.()?.sub,
                  ruolo: store.role?.(),
                },
                on_conflict,
              },
              {
                context: {
                  headers: { 'x-hasura-role': store.role?.() },
                },
              },
            )
            .pipe(
              switchMap((res) =>
                refreshSottoscrizioniMv().pipe(
                  switchMap(() => fetchSottoscrizioni()),
                  map(() => res),
                ),
              ),
              tapResponse({
                next: (res) => res,
                error: (error) => {
                  patchState(store, {
                    failure: error,
                  });
                  _toast.add({
                    key: 'global-toast',
                    severity: 'error',
                    summary: _translate.instant('toast.error.title'),
                    detail: _translate.instant('toast.error.detail', {
                      error,
                    }),
                  });
                  _loading.stop();
                },
              }),
            );
        });
      },
      saveTabellaState: (state: {
        expandedGroups: (string | null)[];
        scrollY: number;
      }) => {
        patchState(store, { tabella_sottoscrizioni_ui: state });
      },
      setTabellaWaitRefreshLoading: (value: boolean) => {
        patchState(store, { tabella_wait_refresh_loading: value });
      },
      setTabellaWaitRefreshTargetVersion: (value?: number) => {
        patchState(store, { tabella_wait_refresh_target_version: value });
      },
      clear: () => {
        clearAllSubs();
        patchState(store, initialStagingState);
      },
      // insertOrUpdateStagingSottoscrizione: (
      //   objects: Staging_Installatore_Sottoscrizione_Insert_Input,
      //   on_conflict: Staging_Installatore_Sottoscrizione_On_Conflict,
      // ) => {
      //   return runInInjectionContext(injector, () => {
      //     const _insertOrUpdate = inject(
      //       InsertOrUpdateStagingInstallatoreSottoscrizioneGQL,
      //     );
      //     const _toast = inject(MessageService);
      //     const _translate = inject(TranslateService);
      //     const _userStore = inject(UserStore);
      //     const _loading = inject(LoadingService);
      //     _loading.start();
      //     return _insertOrUpdate
      //       .mutate(
      //         {
      //           objects: { ...objects, auth_id: _userStore.user_data?.()?.sub },
      //           on_conflict,
      //         },
      //         {
      //           context: {
      //             headers: { 'x-hasura-role': 'installatore' },
      //           },
      //         },
      //       )
      //       .pipe(
      //         tapResponse({
      //           next: (res) => {
      //             _loading.stop();
      //             return res;
      //           },
      //           error: (error) => {
      //             patchState(store, {
      //               failure: error,
      //             });
      //             _loading.stop();
      //             _toast.add({
      //               key: 'global-toast',
      //               severity: 'error',
      //               summary: _translate.instant('toast.error.title'),
      //               detail: _translate.instant('toast.error.detail', {
      //                 error,
      //               }),
      //             });
      //           },
      //         }),
      //       );
      //   });
      // },
      // initStagingSottoscrizioni: () => {
      //   const _getter = inject(GetStagingInstallatoreSottoscrizioniGQL);
      //   const _toast = inject(MessageService);
      //   const _translate = inject(TranslateService);

      //   return runInInjectionContext(injector, () => {
      //     return _getter.fetch(
      //       { where: {} },
      //       {
      //         context: {
      //           headers: { 'x-hasura-role': 'installatore' },
      //         },
      //       },
      //     );
      //   }).subscribe({
      //     next: (res) => {
      //       patchState(store, {
      //         staging_sottoscrizione_loading: false,
      //         staging_sottoscrizione_data: res.data?.staging_sottoscrizione,
      //       });
      //     },
      //     error: (error) => {
      //       patchState(store, {
      //         staging_sottoscrizione_loading: false,
      //         staging_sottoscrizione_data: undefined,
      //         failure: error,
      //       });
      //       _toast.add({
      //         key: 'global-toast',
      //         severity: 'error',
      //         summary: _translate.instant(`toast.error.title`),
      //         detail: _translate.instant(`toast.error.detail`, {
      //           error,
      //         }),
      //       });
      //     },
      //   });
      // },
      // loadSelectedStagingSottoscrizione: (id: string) => {
      //   const _getter = inject(GetStagingInstallatoreSottoscrizioneGQL);
      //   const _toast = inject(MessageService);
      //   const _translate = inject(TranslateService);

      //   return runInInjectionContext(injector, () => {
      //     return _getter.fetch({
      //       id: {
      //         _eq: id,
      //       },
      //     });
      //   }).subscribe({
      //     next: (res) => {
      //       patchState(store, {
      //         staging_sottoscrizione_loading: false,
      //         selected_staging_sottoscrizione:
      //           res.data?.staging_sottoscrizione_by_pk,
      //       });
      //     },
      //     error: (error) => {
      //       patchState(store, {
      //         staging_sottoscrizione_loading: false,
      //         selected_staging_sottoscrizione: undefined,
      //         failure: error,
      //       });
      //       _toast.add({
      //         key: 'global-toast',
      //         severity: 'error',
      //         summary: _translate.instant(`toast.error.title`),
      //         detail: _translate.instant(`toast.error.detail`, {
      //           error,
      //         }),
      //       });
      //     },
      //   });
      // },
      // patchSelectedStagingSottocrizione: (
      //   sottoscrizione: GetStagingInstallatoreSottoscrizioneQuery['staging_sottoscrizione_by_pk'],
      // ) => {
      //   patchState(store, {
      //     selected_staging_sottoscrizione: sottoscrizione,
      //   });
      // },
      // patchSelectedLotto: (
      //   lotto: DeepPartial<
      //     GetStagingInstallatoreLottiQuery['staging_lotto'][0]
      //   >,
      // ) => {
      //   patchState(store, {
      //     selected_lotto: lotto,
      //   });
      // },
      // initStagingLotti() {
      //   return runInInjectionContext(injector, () => {
      //     const _apollo = inject(ApolloRoleService);
      //     const _getter = _apollo.getClient('installatore');
      //     const _toast = inject(MessageService);
      //     const _translate = inject(TranslateService);

      //     clearSub('stagingLotti');

      //     const sub = _getter
      //       .subscribe<GetStagingInstallatoreLottiSubscriptionSubscription>({
      //         query: GetStagingInstallatoreLottiSubscriptionDocument,
      //         variables: {},
      //       })
      //       .subscribe({
      //         next: (res) => {
      //           patchState(store, {
      //             staging_lotti_data: res.data?.staging_lotto,
      //             staging_lotti_loading: false,
      //           });
      //         },
      //         error: (error) => {
      //           patchState(store, {
      //             staging_lotti_data: undefined,
      //             staging_lotti_loading: false,
      //             failure: error,
      //           });
      //           _toast.add({
      //             key: 'global-toast',
      //             severity: 'error',
      //             summary: _translate.instant(`toast.error.title`),
      //             detail: _translate.instant(`toast.error.detail`, {
      //               error,
      //             }),
      //           });
      //         },
      //       });

      //     setSub('stagingLotti', sub);
      //     return sub;
      //   });
      // },
      // getStagingLotti() {
      //   const _getter = inject(GetStagingInstallatoreLottiGQL);
      //   const _toast = inject(MessageService);
      //   const _translate = inject(TranslateService);

      //   return runInInjectionContext(injector, () => {
      //     return _getter.fetch(
      //       {},
      //       {
      //         context: {
      //           headers: { 'x-hasura-role': 'installatore' },
      //         },
      //       },
      //     );
      //   }).subscribe({
      //     next: (res) => {
      //       patchState(store, {
      //         staging_lotti_data: res.data?.staging_lotto,
      //       });
      //     },
      //     error: (error) => {
      //       patchState(store, {
      //         staging_lotti_data: undefined,
      //         failure: error,
      //       });
      //       _toast.add({
      //         key: 'global-toast',
      //         severity: 'error',
      //         summary: _translate.instant(`toast.error.title`),
      //         detail: _translate.instant(`toast.error.detail`, {
      //           error,
      //         }),
      //       });
      //     },
      //   });
      // },
      // updateStatoStagingSottoscrizioni: (
      //   ids: string[],
      //   stato: Staging__Stato_Sottoscrizione_Enum,
      // ) => {
      //   return runInInjectionContext(injector, () => {
      //     const _mutation = inject(UpdateStatoStagingSottoscrizioniGQL);
      //     const _toast = inject(MessageService);
      //     const _translate = inject(TranslateService);
      //     const _loading = inject(LoadingService);

      //     _loading.start();

      //     return _mutation
      //       .mutate(
      //         {
      //           ids,
      //           stato,
      //         },
      //         {
      //           context: {
      //             headers: { 'x-hasura-role': 'installatore' },
      //           },
      //         },
      //       )
      //       .pipe(
      //         tapResponse({
      //           next: (res) => {
      //             _loading.stop();
      //             return res;
      //           },
      //           error: (error) => {
      //             patchState(store, {
      //               failure: error,
      //             });
      //             _loading.stop();
      //             _toast.add({
      //               key: 'global-toast',
      //               severity: 'error',
      //               summary: _translate.instant('toast.error.title'),
      //               detail: _translate.instant('toast.error.detail', {
      //                 error,
      //               }),
      //             });
      //           },
      //         }),
      //       );
      //   });
      // },
      // deleteStagingLottoCompleto: (
      //   sottoscrizione_ids: string[],
      //   lotto_ids: string[],
      // ) => {
      //   return runInInjectionContext(injector, () => {
      //     const _mutation = inject(DeleteStagingLottoCompletoGQL);
      //     const _toast = inject(MessageService);
      //     const _translate = inject(TranslateService);
      //     const _loading = inject(LoadingService);

      //     _loading.start();

      //     return _mutation
      //       .mutate(
      //         {
      //           sottoscrizione_ids,
      //           lotto_ids,
      //         },
      //         {
      //           context: {
      //             headers: { 'x-hasura-role': 'installatore' },
      //           },
      //         },
      //       )
      //       .pipe(
      //         tapResponse({
      //           next: (res) => {
      //             _loading.stop();
      //             _toast.add({
      //               key: 'global-toast',
      //               severity: 'success',
      //               summary: _translate.instant(
      //                 'staging.sottoscrizioni.toast.success.title',
      //               ),
      //               detail: _translate.instant(
      //                 'staging.sottoscrizioni.toast.delete.success.detail',
      //                 {
      //                   count: res.data?.delete_lotto?.affected_rows || 0,
      //                 },
      //               ),
      //             });
      //             return res;
      //           },
      //           error: (error) => {
      //             patchState(store, {
      //               failure: error,
      //             });
      //             _loading.stop();
      //             _toast.add({
      //               key: 'global-toast',
      //               severity: 'error',
      //               summary: _translate.instant(
      //                 'staging.sottoscrizioni.toast.error.title',
      //               ),
      //               detail: _translate.instant(
      //                 'staging.sottoscrizioni.toast.error.detail',
      //                 {
      //                   error,
      //                 },
      //               ),
      //             });
      //           },
      //         }),
      //       );
      //   });
      // },
    };
  }),
);
